import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, InlineNotification, Loading } from '@carbon/react';
import { useAuthSession } from 'src/hooks/useAuthSession';
import { authClient } from 'src/services/auth/client';
import { clearAccessToken } from 'src/services/auth/tokenStore';
import { QUERIES } from 'src/utils/react-query';

export const PENDING_INVITATION_KEY = 'pendingInvitationId';

const AcceptInvitation = () => {
  const { t } = useTranslation();
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session, isPending, isVerified } = useAuthSession();
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  // Most invitees have no account yet. Park the id so the sign-up round trip can
  // resume the acceptance instead of dropping the invitation on the floor.
  useEffect(() => {
    if (invitationId) {
      sessionStorage.setItem(PENDING_INVITATION_KEY, invitationId);
    }
  }, [invitationId]);

  if (isPending) {
    return <Loading withOverlay description={t('invitation.loading')} />;
  }

  async function accept() {
    setWorking(true);
    const { data, error: acceptError } = await authClient.organization.acceptInvitation({
      invitationId: invitationId!,
    });
    setWorking(false);
    if (acceptError) {
      setError(acceptError.message ?? t('invitation.failed'));
      return;
    }
    sessionStorage.removeItem(PENDING_INVITATION_KEY);
    await authClient.organization.setActive({ organizationId: data!.invitation.organizationId });
    clearAccessToken();
    // Refresh the session_data cookie view before the tenant render requests the role.
    await authClient.getSession({ query: { disableCookieCache: true } });
    // The onboarding status cached at the pre-accept mount says "no org"; nothing is
    // observing that query from this page, so invalidating alone would not refetch it and
    // the global query defaults (refetchOnMount: false) would deliver the stale value to
    // the ContextLoader — bouncing the freshly-accepted member to the wizard. Refetch.
    await queryClient.refetchQueries({ queryKey: [QUERIES.MEMBERSHIPS] });
    await queryClient.refetchQueries({ queryKey: [QUERIES.ONBOARDING_STATUS] });
    navigate('/', { replace: true });
  }

  async function decline() {
    await authClient.organization.rejectInvitation({ invitationId: invitationId! });
    sessionStorage.removeItem(PENDING_INVITATION_KEY);
    navigate('/sign-in', { replace: true });
  }

  if (!session) {
    return (
      <div className="cds--auth__form" data-testid="invitation-signed-out">
        <h1>{t('invitation.title')}</h1>
        <p>{t('invitation.signedOutBody')}</p>
        <Button onClick={() => navigate('/sign-up')}>{t('invitation.createAccount')}</Button>
        <Button kind="tertiary" onClick={() => navigate('/sign-in')}>
          {t('invitation.signIn')}
        </Button>
      </div>
    );
  }

  if (!isVerified) {
    return (
      <div className="cds--auth__form">
        <InlineNotification kind="warning" title={t('invitation.verifyFirst')} lowContrast hideCloseButton />
        <Button onClick={() => navigate('/verify-email')}>{t('invitation.goVerify')}</Button>
      </div>
    );
  }

  return (
    <div className="cds--auth__form">
      <h1>{t('invitation.title')}</h1>
      {error && <InlineNotification kind="error" title={error} lowContrast hideCloseButton />}
      <Button disabled={working} onClick={accept} data-testid="invitation-accept">
        {t('invitation.accept')}
      </Button>
      <Button kind="ghost" disabled={working} onClick={decline}>
        {t('invitation.decline')}
      </Button>
    </div>
  );
};

export default AcceptInvitation;
